import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import { getReportById } from '../utils/mockReportsData';
import { advanceStoredStep, areAllModulesCompleted } from '../utils/stepperProgress';
import { buildReportTemplateDocument } from '../utils/reportTemplateGenerator';
import { getDossierByReportId } from '../utils/mockDossierData';
import { MOCK_STATIONS } from '../utils/mockStationsData';
import {
  getDraftStorageKey,
  getSerializedEditorState,
  isLegacyDraftPayload,
} from '../utils/reportDraftStorage';
import { isReportDraftLocalCacheEnabled, loadBestDraft } from '../utils/reportDraftRepository';
import { sanitizeSerializedStateForOfficialExport } from '../utils/reportExportSanitizer';
import { resolveLanguageCode } from '../utils/locale';
import { ReportDraftEditor } from '../components/reports/drafting/ReportDraftEditor';
import './ReportReviewPageNew.css';

export function ReportDraftingFlow({
  report: reportProp,
  isEmbedded = false,
  onBack,
  onContinueToPreview,
  onAutosaveActivity,
  toolbarPortalTarget = null,
  onRegisterPersistenceControls = null,
}) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { navigateTo } = useLanguageNavigation();
  const report = reportProp || getReportById(id);
  const reportId = report?.id || id;
  const allModulesCompleted = areAllModulesCompleted(report);
  const locale = resolveLanguageCode(i18n.language);

  const dossier = useMemo(
    () => (report ? getDossierByReportId(report.id, report.type) : null),
    [report]
  );

  const templateT = useMemo(() => i18n.getFixedT(locale), [i18n, locale]);

  const templatePayload = useMemo(
    () => buildReportTemplateDocument({
      report,
      reportType: report?.type,
      locale,
      t: templateT,
      dossier,
      stations: MOCK_STATIONS,
    }),
    [report, report?.type, locale, templateT, dossier]
  );
  const templateDocument = templatePayload?.document || null;
  const storageKey = useMemo(
    () => getDraftStorageKey(reportId, locale),
    [reportId, locale]
  );
  const [storedDraft, setStoredDraft] = useState(null);
  const [isDraftLoadResolved, setIsDraftLoadResolved] = useState(false);
  const shouldSanitizeStoredDraft = useMemo(
    () => isLegacyDraftPayload(storedDraft),
    [storedDraft]
  );
  const initialSerializedState = useMemo(
    () => {
      const serialized = getSerializedEditorState(storedDraft);
      if (!serialized || !shouldSanitizeStoredDraft) {
        return serialized;
      }
      return sanitizeSerializedStateForOfficialExport(serialized, locale, {
        removeInternalSections: true,
      });
    },
    [storedDraft, shouldSanitizeStoredDraft, locale]
  );
  const initialLastSavedAt = useMemo(() => {
    if (!storedDraft?.timestamp) {
      return null;
    }
    const parsedDate = new Date(storedDraft.timestamp);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }
    return parsedDate;
  }, [storedDraft]);
  const editorKey = useMemo(
    () => `${reportId || 'draft'}:${locale}:${initialSerializedState ? 'saved' : 'template'}`,
    [reportId, locale, initialSerializedState]
  );
  const [draftStats, setDraftStats] = useState({
    wordCount: 0,
    saveStatus: 'saved',
    lastSavedAt: initialLastSavedAt,
  });
  const handleDraftStatsChange = useCallback((nextStats) => {
    setDraftStats((prev) => ({ ...prev, ...nextStats }));
  }, []);
  const persistenceControlsRef = useRef(null);

  const handlePersistenceControlsRegistration = useCallback((controls) => {
    persistenceControlsRef.current = controls && typeof controls === 'object' ? controls : null;
    if (typeof onRegisterPersistenceControls === 'function') {
      onRegisterPersistenceControls(persistenceControlsRef.current);
    }
  }, [onRegisterPersistenceControls]);

  const flushLocalBeforeNavigation = useCallback(async () => {
    const controls = persistenceControlsRef.current;
    const flushLocalNow = controls?.flushLocalNow;
    const flushRemoteNow = controls?.flushRemoteNow;
    if (typeof flushLocalNow !== 'function') {
      return;
    }
    try {
      await Promise.race([
        Promise.resolve().then(async () => {
          await flushLocalNow({ reason: 'navigation', skipRemoteSchedule: true });
          if (!isReportDraftLocalCacheEnabled() && typeof flushRemoteNow === 'function') {
            await flushRemoteNow();
          }
        }),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
    } catch (error) {
      // Best effort: do not block navigation if flush fails.
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;
    setIsDraftLoadResolved(false);
    (async () => {
      let nextDraft = null;
      try {
        nextDraft = await loadBestDraft({ reportId, locale, storageKey });
      } catch (error) {
        nextDraft = null;
      }
      if (isCancelled) {
        return;
      }
      setStoredDraft(nextDraft);
      setIsDraftLoadResolved(true);
    })();
    return () => {
      isCancelled = true;
    };
  }, [locale, reportId, storageKey]);
  useEffect(() => {
    setDraftStats({
      wordCount: 0,
      saveStatus: 'saved',
      lastSavedAt: initialLastSavedAt,
    });
  }, [editorKey, initialLastSavedAt]);

  const handleContinueToPreview = async () => {
    await flushLocalBeforeNavigation();
    if (typeof onContinueToPreview === 'function') {
      onContinueToPreview();
      return;
    }
    if (!id) {
      return;
    }
    advanceStoredStep(id, 'preview');
    navigateTo(`/reports/${id}/preview`);
  };


  const reportIdLabel = t('reports.review.reportId', {
    id: id || t('common.notAvailable'),
  });
  const layoutClassName = 'report-review-page-new__layout';
  const lastSavedAt = draftStats.lastSavedAt;
  const lastSavedValue = (() => {
    if (!lastSavedAt) {
      return t('reports.review.editor.footer.justNow');
    }
    const timeDiff = Date.now() - lastSavedAt.getTime();
    if (Number.isNaN(timeDiff) || timeDiff < 60 * 1000) {
      return t('reports.review.editor.footer.justNow');
    }
    try {
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(lastSavedAt);
    } catch (error) {
      return lastSavedAt.toLocaleTimeString();
    }
  })();
  const lastSavedLabel = `${t('reports.review.editor.footer.lastSaved')}: ${lastSavedValue}`;
  const wordCountLabel = t('reports.review.editor.footer.words', {
    count: draftStats.wordCount,
  });

  const headerContent = (
    <div className="report-review-page-new__header">
      <div className="report-review-page-new__header-copy">
        <h1>{t('reports.review.title')}</h1>
        <p>{t('reports.review.subtitle')}</p>
        <div className="report-review-page-new__header-meta">{reportIdLabel}</div>
      </div>
      <div className="report-review-page-new__header-actions">
        <Button
          variant="primary"
          size="md"
          type="button"
          onClick={handleContinueToPreview}
          disabled={!allModulesCompleted}
        >
          {t('reports.review.continueToPreview')}
        </Button>
      </div>
    </div>
  );

  const documentHeader = !isEmbedded ? (
    <div className="report-review-page-new__document-header">
      <div className="report-review-page-new__document-info">
        <h2>{report?.name || t('reports.workspace.fallback.report')}</h2>
        <p>{t('reports.review.templateHint')}</p>
      </div>
    </div>
  ) : null;
  const embeddedFooter = (
    <div className="report-drafting-flow__footer">
      {typeof onBack === 'function' && (
        <Button
          variant="secondary"
          size="md"
          icon={<ArrowLeft size={16} />}
          onClick={async () => {
            await flushLocalBeforeNavigation();
            onBack();
          }}
        >
          {t('common.back')}
        </Button>
      )}
      <div className="report-drafting-flow__footer-meta">
        <span>{lastSavedLabel}</span>
        <span aria-hidden="true">·</span>
        <span>{wordCountLabel}</span>
      </div>
      <Button
        variant="primary"
        size="md"
        type="button"
        onClick={handleContinueToPreview}
        disabled={!allModulesCompleted}
      >
        {t('reports.review.continueToPreview')}
      </Button>
    </div>
  );
  const documentContent = (
    <div className="report-review-page-new__document">
      {documentHeader}
      {isDraftLoadResolved ? (
        <ReportDraftEditor
          key={editorKey}
          templateDocument={templateDocument}
          initialSerializedState={initialSerializedState}
          storageKey={storageKey}
          reportId={reportId}
          locale={locale}
          onAutosave={onAutosaveActivity}
          onRegisterPersistenceControls={handlePersistenceControlsRegistration}
          initialDraftPayload={storedDraft}
          initialLastSavedAt={initialLastSavedAt}
          onStatsChange={handleDraftStatsChange}
          footerContent={isEmbedded ? embeddedFooter : null}
          paginationMode="continuous"
          paginationProfile="screen"
          toolbarPortalTarget={toolbarPortalTarget}
        />
      ) : (
        <div className="report-review-page-new__document-loading">
          {t('common.loading', { defaultValue: 'Loading draft...' })}
        </div>
      )}
    </div>
  );

  if (!isEmbedded) {
    return (
      <div className="report-review-page-new">
        <div className="report-review-page-new__container">
          {headerContent}
          <div className={layoutClassName}>
            <div className="report-review-page-new__workspace">
              {documentContent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-review-page-new report-review-page-new--embedded">
      <div className={layoutClassName}>
        <div className="report-review-page-new__workspace report-review-page-new__workspace--embedded">
          <div className="report-drafting-flow">
            <div className="report-drafting-flow__editor-wrapper">
              {documentContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReportReviewPageNew() {
  return <ReportDraftingFlow />;
}

export default ReportReviewPageNew;
